<template>
  <div class="plan-card">
    <div class="plan-details">
      <header class="plan-header">
        <h5 class="plan-name">{{ name }}</h5>
        <p class="content-hint plan-tagline">{{ tagline }}</p>
      </header>

      <div class="plan-pricing">
        <div v-if="loading" class="plan-price-loading">
          <v-progress-circular size="24" indeterminate color="primary" role="status" :aria-label="$t('plans_price_loading')"></v-progress-circular>
        </div>
        <p v-else class="plan-price-line">
          <span class="plan-price">{{ price }}</span>
          <span v-if="pricePeriod" class="plan-price-period">{{ pricePeriod }}</span>
        </p>
        <p v-if="!loading" class="content-hint plan-price-note">{{ priceNote }}</p>
      </div>
    </div>

    <v-btn
      class="plan-cta"
      :height="43"
      color="primary"
      :outlined="isOutlined"
      :depressed="!isOutlined"
      :disabled="ctaDisabled"
      @click="$emit('cta')">{{ ctaLabel }}</v-btn>

    <div v-if="$slots['after-cta']" class="plan-after-cta">
      <slot name="after-cta"></slot>
    </div>

    <div class="plan-included">
      <p class="content-hint uppercase plan-section-label">{{ $t('plans_included') }}</p>
      <dl class="plan-quotas">
        <div v-for="quota in quotas" :key="quota.label" class="plan-quota">
          <dt>{{ quota.label }}</dt>
          <dd>{{ quota.value }}</dd>
        </div>
      </dl>
    </div>

    <div class="plan-features">
      <p class="plan-features-title">{{ featuresTitle }}</p>
      <ul>
        <li v-for="feature in features" :key="feature">{{ feature }}</li>
      </ul>
    </div>
  </div>
</template>

<script>

export default {
  name: 'PlanCard',
  props: {
    name: {
      type: String,
      required: true
    },
    tagline: {
      type: String,
      required: true
    },
    price: {
      type: String,
      default: ''
    },
    pricePeriod: {
      type: String,
      default: ''
    },
    priceNote: {
      type: String,
      required: true
    },
    quotas: {
      type: Array,
      required: true
    },
    featuresTitle: {
      type: String,
      required: true
    },
    features: {
      type: Array,
      required: true
    },
    ctaLabel: {
      type: String,
      required: true
    },
    ctaDisabled: {
      type: Boolean,
      default: false
    },
    ctaVariant: {
      type: String,
      default: 'filled',
      validator: (value) => ['filled', 'outlined'].includes(value)
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isOutlined () {
      return this.ctaVariant === 'outlined'
    }
  }
}
</script>

<style lang="sass" scoped>
@import '@/variables.sass'

.plan-card
  display: flex
  flex-direction: column
  width: 100%
  padding: 15px 20px
  background: white
  border-radius: 6px
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.1)
  text-align: left
  > * + *
    margin-top: 20px

.plan-details
  > * + *
    margin-top: 8px
.plan-header
  display: flex
  flex-direction: column
  min-height: 80px
.plan-pricing
  min-height: 73px
.plan-name
  margin: 0
  font-size: 28px
  line-height: normal
  font-weight: 700
  letter-spacing: 0.8px
  color: $primary
.plan-tagline
  margin: 0
  line-height: normal
.plan-cta
  width: 100%

.plan-price-line
  display: flex
  align-items: baseline
  margin: 0
.plan-price
  font-size: 32px
  font-weight: 700
  line-height: 40px
  letter-spacing: 0.8px
  color: $text-black
.plan-price-period
  margin-left: 5px
  font-size: 16px
  line-height: 20px
  color: $text-black
.plan-price-note
  margin: 8px 0 0
  line-height: 20px
.plan-price-loading
  height: 40px
  display: flex
  align-items: center
.plan-section-label
  margin: 0 0 20px
.plan-quotas
  margin: 0
.plan-quota
  display: flex
  align-items: center
  justify-content: space-between
  padding: 8px 0
  & + &
    border-top: 1px solid $divider-grey
  &:first-child
    padding-top: 0
  &:last-child
    padding-bottom: 0
  dt
    font-size: 16px
    letter-spacing: 0.8px
    color: $text-black
  dd
    margin: 0
    font-size: 16px
    font-weight: 600
    letter-spacing: 0.8px
    color: $text-black
.plan-features-title
  margin: 0 0 8px
  font-size: 16px
  font-weight: 600
  letter-spacing: 0.8px
  color: $text-black
.plan-features
  ul
    margin: 0
    padding-left: 21px
  li
    font-size: 14px
    line-height: 2
    letter-spacing: 0.5px
    color: $text-black

@include lg
  .plan-card
    display: contents
  .plan-details
    grid-row: 1
    padding: 15px 20px 0
  .plan-cta
    grid-row: 2
    width: auto
    margin-left: 20px
    margin-right: 20px
  .plan-after-cta
    grid-row: 3
    padding: 0 20px
  .plan-included
    grid-row: 4
    padding: 0 20px
  .plan-features
    grid-row: 5
    padding: 0 20px 15px
</style>
